import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { SPORTS } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const GroupSwitch = ({ teams, onToggle, testId }) => {
  const on = teams.filter((t) => t.followed).length;
  return (
    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
      <span className="font-data text-[11px] text-slate-500">{on}/{teams.length}</span>
      <Switch data-testid={testId} checked={on === teams.length && on > 0} onCheckedChange={(v) => onToggle(teams, v)} />
    </div>
  );
};

const TeamRow = ({ t, onToggle }) => (
  <div data-testid={`coverage-team-${t.ff_id}`} className="flex items-center justify-between gap-3 py-2.5 pl-2">
    <div className="flex min-w-0 items-center gap-3">
      {t.logo_url ? <img src={t.logo_url} alt="" className="h-7 w-7 shrink-0 object-contain" />
        : <span className="h-7 w-7 shrink-0 rounded-full" style={{ background: t.color_primary || "#334155" }} />}
      <span className="truncate text-sm">{t.name}</span>
    </div>
    <Switch data-testid={`coverage-team-switch-${t.ff_id}`} checked={!!t.followed} onCheckedChange={(v) => onToggle([t], v)} />
  </div>
);

export const CoverageTab = () => {
  const [teams, setTeams] = useState(null);
  const [q, setQ] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const load = () => api.get("/admin/fanflare/teams").then((r) => setTeams(r.data.items)).catch((e) => { toast.error(formatApiError(e)); setTeams([]); });
  useEffect(() => { load(); }, []);

  const toggle = async (list, followed) => {
    const ids = new Set(list.map((t) => t.ff_id));
    setTeams((all) => all.map((t) => (ids.has(t.ff_id) ? { ...t, followed } : t)));
    try {
      await api.put("/admin/fanflare/coverage", { team_ids: [...ids], followed });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { await api.post("/admin/fanflare/teams/refresh"); await load(); toast.success("Team list refreshed"); }
    catch (e) { toast.error(formatApiError(e)); }
    setRefreshing(false);
  };

  const grouped = useMemo(() => {
    const term = q.trim().toLowerCase();
    const out = {};
    (teams || []).filter((t) => !term || t.name.toLowerCase().includes(term) || (t.conference_name || "").toLowerCase().includes(term))
      .forEach((t) => {
        const conf = t.conference_name || "Independent";
        ((out[t.sport] ||= {})[conf] ||= []).push(t);
      });
    return out;
  }, [teams, q]);

  const followed = (teams || []).filter((t) => t.followed).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="coverage-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teams or conferences"
            className="h-11 w-full rounded-lg border border-slate-800 bg-[#131927] pl-11 pr-4 text-sm outline-none focus:border-[#FF2D78]" />
        </div>
        <span data-testid="coverage-followed-count" className="font-data text-xs text-slate-400">{followed} teams switched on</span>
        <button data-testid="coverage-refresh-button" onClick={refresh} disabled={refreshing}
          className="inline-flex h-11 items-center gap-2 rounded-full border border-slate-700 px-4 text-sm transition-colors hover:border-[#FF2D78] disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh teams
        </button>
      </div>
      <p className="text-sm text-slate-400">Switch on the teams to import. Changes apply on the next sync. Players from teams you switch off stay on the site but stop updating.</p>
      {!teams && <p className="text-sm text-slate-500">Loading teams from FanFlare</p>}
      {teams && !teams.length && <p data-testid="coverage-empty" className="text-sm text-slate-500">No teams available. Check the FanFlare connection.</p>}
      {Object.entries(grouped).map(([sport, confs]) => {
        const all = Object.values(confs).flat();
        return (
          <section key={sport} data-testid={`coverage-sport-${sport}`} className="rounded-xl border border-slate-800 bg-[#131927]">
            <div className="flex items-center justify-between gap-4 border-b border-slate-800 p-5">
              <div>
                <h2 className="font-display text-2xl font-extrabold uppercase">{SPORTS[sport]?.label}</h2>
                <p className="font-data text-[11px] uppercase tracking-widest text-slate-500">NCAA Division I · {Object.keys(confs).length} conferences</p>
              </div>
              <GroupSwitch teams={all} onToggle={toggle} testId={`coverage-sport-switch-${sport}`} />
            </div>
            <Accordion type="multiple" className="px-5">
              {Object.entries(confs).sort(([a], [b]) => a.localeCompare(b)).map(([conf, list]) => (
                <AccordionItem key={conf} value={conf} className="border-slate-800">
                  <div className="flex items-center gap-4">
                    <AccordionTrigger data-testid={`coverage-conf-${sport}-${slug(conf)}`} className="flex-1 py-4 text-sm font-semibold hover:no-underline">{conf}</AccordionTrigger>
                    <GroupSwitch teams={list} onToggle={toggle} testId={`coverage-conf-switch-${sport}-${slug(conf)}`} />
                  </div>
                  <AccordionContent>
                    <div className="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">{list.map((t) => <TeamRow key={t.ff_id} t={t} onToggle={toggle} />)}</div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        );
      })}
    </div>
  );
};
